import React, { useEffect, useMemo, useState } from 'react';
import { Cpu, Flame, MessageSquare, Radio, ShieldAlert, Sparkles, StopCircle, Target } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { usePilotContext } from '../../hooks/usePilotContext';
import { useAuth } from '../../contexts/AuthContext';
import { useAertagoEmbeddedAgentWs } from '../../hooks/useAertagoEmbeddedAgentWs';
import { api } from '../../lib/api';

const FEED_TYPES = new Set([
  'thinking',
  'thinking_chunk',
  'deep_think',
  'tool_start',
  'tool_output_chunk',
  'tool_complete',
  'file_ready',
  'phase_update',
  'error',
  'response',
  'task_complete',
  'plan_start',
  'plan_complete',
  'plan_analysis',
  'execution_step',
  'guidance_ack',
  'stopped',
]);

function eventAccent(type) {
  const t = String(type || '');
  if (t === 'error') return 'border-l-rose-500/70 bg-rose-500/[0.06]';
  if (t === 'response' || t === 'task_complete') return 'border-l-emerald-500/60 bg-emerald-500/[0.05]';
  if (t.startsWith('tool_')) return 'border-l-amber-500/60 bg-amber-500/[0.05]';
  if (t === 'plan_start' || t === 'plan_complete' || t === 'plan_analysis') return 'border-l-sky-500/50 bg-sky-500/[0.05]';
  if (t === 'deep_think') return 'border-l-fuchsia-500/50 bg-fuchsia-500/[0.05]';
  if (t === 'thinking' || t === 'thinking_chunk') return 'border-l-violet-500/50 bg-violet-500/[0.05]';
  return 'border-l-border bg-background/50';
}

function TinySkeleton({ className }) {
  return <div className={`h-2.5 w-full animate-pulse rounded bg-muted/40 motion-reduce:animate-none ${className || ''}`} />;
}

function summarizeEvent(type, payload) {
  if (payload == null) return '—';
  if (typeof payload === 'string') return payload.slice(0, 400);
  if (typeof payload !== 'object') return String(payload);
  if (type === 'tool_start') {
    return `${payload.tool_name || 'tool'}(${JSON.stringify(payload.tool_args || {}).slice(0, 120)}…)`;
  }
  if (type === 'tool_output_chunk') return String(payload.chunk || '').slice(0, 800) || '—';
  if (type === 'tool_complete') return String(payload.output_summary || '').slice(0, 800) || JSON.stringify(payload).slice(0, 400);
  if (type === 'thinking') {
    return [payload.thought, payload.reasoning].filter(Boolean).join('\n\n').slice(0, 800) || '—';
  }
  if (type === 'deep_think') return String(payload.analysis || '').slice(0, 900) || JSON.stringify(payload).slice(0, 400);
  if (type === 'plan_start') return `${payload.tool_count ?? '—'} tool(s) · ${String(payload.plan_rationale || '').slice(0, 350)}`;
  if (type === 'plan_complete') return `done · ok=${payload.successful ?? '—'} fail=${payload.failed ?? '—'} (total ${payload.total_steps ?? '—'})`;
  if (type === 'plan_analysis') return String(payload.interpretation || '').slice(0, 900) || JSON.stringify(payload).slice(0, 400);
  if (type === 'file_ready') return payload.filepath || payload.filename || JSON.stringify(payload).slice(0, 400);
  if (type === 'error') return payload.message || JSON.stringify(payload);
  if (type === 'response') return String(payload.answer || '').slice(0, 600) || JSON.stringify(payload).slice(0, 400);
  if (type === 'task_complete') return payload.message || JSON.stringify(payload).slice(0, 400);
  if (type === 'phase_update') {
    return `${payload.current_phase || 'phase'} · iter ${payload.iteration_count ?? '—'}`;
  }
  const j = JSON.stringify(payload);
  return j.length > 500 ? `${j.slice(0, 497)}…` : j;
}

function normalizeTodoItem(item, idx) {
  if (item == null) return null;
  if (typeof item === 'string') {
    const d = item.trim();
    if (!d) return null;
    return { id: `derived-${idx}`, description: d, status: 'pending', priority: 'medium' };
  }
  if (typeof item !== 'object') return { id: `todo-${idx}`, description: String(item), status: 'pending', priority: 'medium' };
  const id = String(item.id || `todo-${idx}`);
  const description = String(item.description || item.text || item.title || JSON.stringify(item)).trim();
  const status = String(item.status || 'pending').toLowerCase();
  const priority = String(item.priority || 'medium').toLowerCase();
  return { id, description, status, priority, raw: item };
}

function groupTodos(items) {
  const groups = { pending: [], in_progress: [], completed: [], blocked: [] };
  for (const it of items) {
    const s = it.status;
    if (s === 'in_progress') groups.in_progress.push(it);
    else if (s === 'completed') groups.completed.push(it);
    else if (s === 'blocked') groups.blocked.push(it);
    else groups.pending.push(it);
  }
  return groups;
}

function priorityBadgeVariant(p) {
  if (p === 'high') return 'destructive';
  if (p === 'low') return 'outline';
  return 'secondary';
}

function normalizeAllowlist(list) {
  if (!Array.isArray(list)) return [];
  return [...new Set(list.map((x) => String(x || '').trim()).filter(Boolean))];
}

/** Plain-text prefix for the mission prompt — matches AERTAGO Live Control + pilot network scan. */
export function buildAertagoLabContextBlock({ selectedTarget, allowlistTargets, networkScanTracked }) {
  const st = String(selectedTarget || '').trim();
  const allow = normalizeAllowlist(allowlistTargets);
  const tr = networkScanTracked;
  if (!st && !allow.length && !tr?.jobId) return '';
  const lines = ['[AERTAGO lab context — operator-supplied facts for this session]'];
  if (st) lines.push(`Selected viewer / lab target: ${st}`);
  if (allow.length) {
    const shown = allow.slice(0, 32);
    lines.push(
      `Allowlisted targets (${allow.length}): ${shown.join(', ')}${allow.length > shown.length ? ' …' : ''}`,
    );
  }
  if (tr?.jobId) {
    const status = tr.job?.status || '—';
    const nHosts = Array.isArray(tr.devices) ? tr.devices.length : tr.job?.devices_found;
    lines.push(
      `Pilot network scan: job ${String(tr.jobId).slice(0, 14)}${String(tr.jobId).length > 14 ? '…' : ''} · status=${status}${
        nHosts != null && nHosts !== '' ? ` · hosts=${nHosts}` : ''
      }`,
    );
    if (Array.isArray(tr.devices) && tr.devices.length) {
      const ips = [...new Set(tr.devices.map((d) => String(d?.ip || '').trim()).filter(Boolean))].slice(0, 12);
      if (ips.length) lines.push(`Discovered IPs (sample): ${ips.join(', ')}`);
    }
    if (tr.targetsLabel) lines.push(`Scan scope label: ${tr.targetsLabel}`);
  }
  lines.push('');
  return lines.join('\n');
}

/**
 * AERTAGO-facing **AERTAX-master-class** Red AI console: same WebSocket message types as the
 * standalone AERTAX webapp, backed by the vendored agent on `/api/dojo-lab/aertago-agent/ws`.
 *
 * Pass `labSelectedTarget`, `labAllowlistTargets`, and `networkScanTracked` from Cyber Range so the
 * console shows the same AERTAGO scope as Live Control (selected target + discovery).
 */
export function AertaxRedAiConsole({ caps, labSelectedTarget, labAllowlistTargets, networkScanTracked }) {
  const { context } = usePilotContext();
  const { user } = useAuth();
  const ws = useAertagoEmbeddedAgentWs();
  const [autoConnectTried, setAutoConnectTried] = useState(false);

  const [toolRunsExpanded, setToolRunsExpanded] = useState(() => new Set());
  const [toolRunsShowOutput, setToolRunsShowOutput] = useState(true);

  const [consoleSelectedTarget, setConsoleSelectedTarget] = useState('');
  const [allowlistLocal, setAllowlistLocal] = useState([]);

  const [scanTargets, setScanTargets] = useState('');
  const [scanPorts, setScanPorts] = useState('');
  const [scanIncludeNoPorts, setScanIncludeNoPorts] = useState(true);
  const [scanAutoIngest, setScanAutoIngest] = useState(true);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanJobId, setScanJobId] = useState('');
  const [scanJob, setScanJob] = useState(null);
  const [scanDevices, setScanDevices] = useState(null);
  const [scanError, setScanError] = useState('');

  const [webUrl, setWebUrl] = useState('http://juice-shop:3000');
  const [webDeep, setWebDeep] = useState(true);
  const [webLoading, setWebLoading] = useState(false);
  const [webResult, setWebResult] = useState(null);
  const [webError, setWebError] = useState('');

  const [projectId, setProjectId] = useState('');
  const [sessionId, setSessionId] = useState(() =>
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `sess-${Date.now()}`,
  );
  const [graphCypher, setGraphCypher] = useState('');
  const [query, setQuery] = useState(
    'Summarize offensive tooling available in this deployment and what requires human approval before use.',
  );
  const [guidance, setGuidance] = useState('');
  const [approvalModify, setApprovalModify] = useState('');
  const [questionAnswer, setQuestionAnswer] = useState('');
  const [toolModifyJson, setToolModifyJson] = useState('{}');

  useEffect(() => {
    const cid = context?.customer_id;
    if (cid && !projectId) {
      setProjectId(String(cid));
    }
  }, [context?.customer_id, projectId]);

  // Auto-connect once (makes first query feel instant).
  useEffect(() => {
    if (autoConnectTried) return;
    if (!caps?.enabled) return;
    if (ws.wsState === 'open' || ws.wsState === 'connecting') return;
    const tokenOk = api.token || (typeof localStorage !== 'undefined' ? localStorage.getItem('aerta_token') : null);
    if (!tokenOk) return;
    setAutoConnectTried(true);
    ws.connect({
      userId: user?.email || user?.sub || `user-${context?.customer_id || 'default'}`,
      projectId: projectId || context?.customer_id || 'default',
      sessionId,
      graphViewCypher: graphCypher.trim() || undefined,
    });
  }, [
    autoConnectTried,
    caps?.enabled,
    context?.customer_id,
    graphCypher,
    projectId,
    sessionId,
    user?.email,
    user?.sub,
    ws,
  ]);

  useEffect(() => {
    setAllowlistLocal(normalizeAllowlist(labAllowlistTargets));
  }, [labAllowlistTargets]);

  useEffect(() => {
    const st = String(labSelectedTarget || '').trim();
    if (st && !consoleSelectedTarget) setConsoleSelectedTarget(st);
  }, [labSelectedTarget, consoleSelectedTarget]);

  useEffect(() => {
    if (!context?.customer_id) return;
    if (scanTargets.trim()) return;
    api
      .get('/api/network-scan/detect-subnets')
      .then((d) => {
        if (d?.recommended_cidr) setScanTargets(String(d.recommended_cidr));
      })
      .catch(() => {
        /* ignore */
      });
  }, [context?.customer_id, scanTargets]);

  const feed = useMemo(() => {
    return ws.events.filter((e) => FEED_TYPES.has(e.type));
  }, [ws.events]);

  const liveStatus = useMemo(() => {
    const rev = [...ws.events].reverse();
    const lastToolStart = rev.find((e) => e.type === 'tool_start' && e.payload);
    const lastToolComplete = rev.find((e) => e.type === 'tool_complete' && e.payload);
    const toolStartAt = lastToolStart?.at || 0;
    const toolDoneAt = lastToolComplete?.at || 0;
    const toolRunning = toolStartAt > toolDoneAt;
    const toolName = toolRunning ? String(lastToolStart?.payload?.tool_name || 'tool') : '';
    const thinking = Boolean(ws.streamingThought);
    const handshaking = ws.wsState === 'open' && !ws.agentInited;
    if (handshaking) return { kind: 'handshaking', label: 'handshaking…' };
    if (toolRunning) return { kind: 'tool', label: `running ${toolName}` };
    if (thinking) return { kind: 'thinking', label: 'thinking…' };
    if (ws.agentInited) return { kind: 'ready', label: 'ready' };
    if (ws.wsState === 'connecting') return { kind: 'connecting', label: 'connecting…' };
    return { kind: 'offline', label: 'offline' };
  }, [ws.agentInited, ws.events, ws.streamingThought, ws.wsState]);

  const latestThinkingText = useMemo(() => {
    const last = [...ws.events].reverse().find((e) => e.type === 'thinking' && e.payload && typeof e.payload === 'object');
    if (!last) return '';
    const p = last.payload;
    return [p.thought, p.reasoning].filter(Boolean).join('\n\n').trim();
  }, [ws.events]);

  const derivedTodos = useMemo(() => {
    if (Array.isArray(ws.todoList) && ws.todoList.length) return [];
    const out = [];

    const lastToolComplete = [...ws.events].reverse().find((e) => e.type === 'tool_complete' && e.payload && typeof e.payload === 'object');
    if (lastToolComplete?.payload) {
      const p = lastToolComplete.payload;
      const steps = Array.isArray(p.recommended_next_steps) ? p.recommended_next_steps : [];
      for (const s of steps.slice(0, 10)) {
        const t = String(s || '').trim();
        if (t) out.push(t);
      }
    }

    const lastPlanAnalysis = [...ws.events].reverse().find((e) => e.type === 'plan_analysis' && e.payload && typeof e.payload === 'object');
    if (lastPlanAnalysis?.payload) {
      const p = lastPlanAnalysis.payload;
      const steps = Array.isArray(p.recommended_next_steps) ? p.recommended_next_steps : [];
      for (const s of steps.slice(0, 10)) {
        const t = String(s || '').trim();
        if (t) out.push(t);
      }
    }

    return [...new Set(out)].slice(0, 20);
  }, [ws.events, ws.todoList]);

  const todosNormalized = useMemo(() => {
    const raw = Array.isArray(ws.todoList) && ws.todoList.length ? ws.todoList : derivedTodos;
    return raw.map((it, idx) => normalizeTodoItem(it, idx)).filter(Boolean);
  }, [ws.todoList, derivedTodos]);

  const todoGroups = useMemo(() => groupTodos(todosNormalized), [todosNormalized]);

  const toolRuns = useMemo(() => {
    const runs = new Map();

    const ensureRun = (key, init) => {
      if (!runs.has(key)) runs.set(key, init);
      return runs.get(key);
    };

    const keyFor = (p, fallbackId) => {
      const wave = p?.wave_id != null ? String(p.wave_id) : '';
      const step = p?.step_index != null ? String(p.step_index) : '';
      const tn = p?.tool_name != null ? String(p.tool_name) : '';
      if (wave || step) return `${wave}:${step}:${tn}`;
      return tn ? `tool:${tn}:${fallbackId}` : `tool:unknown:${fallbackId}`;
    };

    for (const e of ws.events) {
      if (!e || !e.type) continue;
      const p = e.payload && typeof e.payload === 'object' ? e.payload : {};
      if (e.type === 'tool_start') {
        const key = keyFor(p, e.id);
        const run = ensureRun(key, {
          key,
          tool_name: String(p.tool_name || 'tool'),
          tool_args: p.tool_args || {},
          wave_id: p.wave_id != null ? String(p.wave_id) : null,
          step_index: p.step_index != null ? Number(p.step_index) : null,
          started_at: e.at,
          status: 'running',
          output: '',
          output_chars: 0,
          chunks: 0,
          complete: null,
        });
        run.tool_args = p.tool_args || run.tool_args;
        run.started_at = run.started_at || e.at;
        run.status = run.complete ? run.status : 'running';
      } else if (e.type === 'tool_output_chunk') {
        const key = keyFor(p, e.id);
        const run = ensureRun(key, {
          key,
          tool_name: String(p.tool_name || 'tool'),
          tool_args: {},
          wave_id: p.wave_id != null ? String(p.wave_id) : null,
          step_index: p.step_index != null ? Number(p.step_index) : null,
          started_at: null,
          status: 'running',
          output: '',
          output_chars: 0,
          chunks: 0,
          complete: null,
        });
        const chunk = String(p.chunk || '');
        if (chunk) {
          run.output += chunk;
          run.output_chars += chunk.length;
          run.chunks += 1;
          // Keep output bounded in UI memory.
          const cap = 20000;
          if (run.output.length > cap) {
            run.output = run.output.slice(run.output.length - cap);
          }
        }
      } else if (e.type === 'tool_complete') {
        const key = keyFor(p, e.id);
        const run = ensureRun(key, {
          key,
          tool_name: String(p.tool_name || 'tool'),
          tool_args: p.tool_args || {},
          wave_id: p.wave_id != null ? String(p.wave_id) : null,
          step_index: p.step_index != null ? Number(p.step_index) : null,
          started_at: null,
          status: 'complete',
          output: '',
          output_chars: 0,
          chunks: 0,
          complete: null,
        });
        run.complete = p;
        run.tool_args = p.tool_args || run.tool_args;
        run.status = p.success === false ? 'failed' : 'complete';
      }
    }

    const arr = [...runs.values()];
    arr.sort((a, b) => (b.started_at || 0) - (a.started_at || 0));
    return arr;
  }, [ws.events]);

  const runningCount = useMemo(() => toolRuns.filter((r) => r.status === 'running').length, [toolRuns]);

  const toolWaves = useMemo(() => {
    const waves = new Map();

    const ensure = (waveId) => {
      const id = String(waveId || '');
      if (!id) return null;
      if (!waves.has(id)) {
        waves.set(id, {
          wave_id: id,
          started_at: null,
          plan_rationale: '',
          tool_count: null,
          tools: [],
          complete: null,
          analysis: null,
        });
      }
      return waves.get(id);
    };

    for (const e of ws.events) {
      const p = e?.payload && typeof e.payload === 'object' ? e.payload : null;
      if (!p) continue;
      if (e.type === 'plan_start' && p.wave_id) {
        const w = ensure(p.wave_id);
        if (!w) continue;
        w.started_at = w.started_at || e.at;
        w.plan_rationale = String(p.plan_rationale || w.plan_rationale || '');
        w.tool_count = p.tool_count != null ? Number(p.tool_count) : w.tool_count;
        w.tools = Array.isArray(p.tools) ? p.tools : w.tools;
      }
      if (e.type === 'plan_complete' && p.wave_id) {
        const w = ensure(p.wave_id);
        if (!w) continue;
        w.complete = p;
      }
      if (e.type === 'plan_analysis' && p.wave_id) {
        const w = ensure(p.wave_id);
        if (!w) continue;
        w.analysis = p;
      }
    }

    const arr = [...waves.values()];
    arr.sort((a, b) => (b.started_at || 0) - (a.started_at || 0));
    return arr;
  }, [ws.events]);

  const toolRunsByWave = useMemo(() => {
    const by = new Map();
    for (const r of toolRuns) {
      const wid = r.wave_id ? String(r.wave_id) : '';
      if (!wid) continue;
      if (!by.has(wid)) by.set(wid, []);
      by.get(wid).push(r);
    }
    for (const v of by.values()) {
      v.sort((a, b) => (a.step_index ?? 999999) - (b.step_index ?? 999999));
    }
    return by;
  }, [toolRuns]);

  const adHocToolRuns = useMemo(() => toolRuns.filter((r) => !r.wave_id), [toolRuns]);

  const allowlistNorm = useMemo(() => normalizeAllowlist(allowlistLocal.length ? allowlistLocal : labAllowlistTargets), [allowlistLocal, labAllowlistTargets]);

  const effectiveSelectedTarget = String(consoleSelectedTarget || labSelectedTarget || '').trim();

  const hasLabScopeFacts = Boolean(
    effectiveSelectedTarget || allowlistNorm.length || networkScanTracked?.jobId,
  );

  const insertLabContextIntoPrompt = () => {
    const block = buildAertagoLabContextBlock({
      selectedTarget: effectiveSelectedTarget,
      allowlistTargets: allowlistNorm,
      networkScanTracked,
    });
    if (!String(block || '').replace(/\s+/g, ' ').trim()) return;
    setQuery((prev) => {
      if (String(prev).includes('[AERTAGO lab context')) return prev;
      return `${block}---\n${prev}`;
    });
  };

  const toolConfirmationRequest = ws.toolConfirmationRequest;
  const toolsPending = toolConfirmationRequest?.tools;

  const scanServerOn = Boolean(context?.network_scan_enabled);
  const allowlistBlocksScans = context?.network_scan_allowlist_gate_open === false;

  const runWebPentest = async () => {
    if (!context?.customer_id) return;
    const url = String(webUrl || '').trim();
    if (!url) {
      setWebError('Enter a URL (e.g. http://juice-shop:3000 or https://demo.owasp-juice.shop).');
      return;
    }
    setWebError('');
    setWebResult(null);
    setWebLoading(true);
    try {
      const res = await api.post(`/api/pentest-executor/${encodeURIComponent(context.customer_id)}/web-run`, {
        url,
        deep: webDeep,
      });
      setWebResult(res?.web_findings || res);
    } catch (e) {
      setWebError(e?.message || 'Web pentest failed');
    } finally {
      setWebLoading(false);
    }
  };

  const queueScan = async () => {
    if (!context?.customer_id) return;
    const tks = scanTargets
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (tks.length === 0) {
      setScanError('Enter a CIDR or IP (e.g. 192.168.1.0/24).');
      return;
    }
    const portList = scanPorts
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
    setScanError('');
    setScanDevices(null);
    setScanLoading(true);
    try {
      const res = await api.post(`/api/network-scan/${encodeURIComponent(context.customer_id)}/jobs`, {
        targets: tks,
        ports: portList.length ? portList : undefined,
        auto_ingest: scanAutoIngest,
        include_no_ports: scanIncludeNoPorts,
      });
      setScanJobId(res?.job_id || '');
      setScanJob({ job_id: res?.job_id, status: 'queued' });
    } catch (e) {
      setScanError(e?.message || 'Queue failed');
    } finally {
      setScanLoading(false);
    }
  };

  useEffect(() => {
    if (!scanJobId || !context?.customer_id) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const j = await api.get(`/api/network-scan/${encodeURIComponent(context.customer_id)}/jobs/${encodeURIComponent(scanJobId)}`);
        if (cancelled) return;
        setScanJob(j);
        if (j?.status === 'completed') {
          try {
            const devs = await api.get(
              `/api/network-scan/${encodeURIComponent(context.customer_id)}/jobs/${encodeURIComponent(scanJobId)}/devices`,
            );
            if (!cancelled) setScanDevices(Array.isArray(devs?.devices) ? devs.devices : devs);
          } catch (e) {
            if (!cancelled) setScanError(e?.message || 'Could not load devices');
          }
        }
        if (j?.status === 'completed' || j?.status === 'failed') return;
      } catch (e) {
        if (!cancelled) setScanError(e?.message || 'Status poll failed');
      }
      if (!cancelled) setTimeout(poll, 2500);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [scanJobId, context?.customer_id]);

  const persistAllowlist = async (targets) => {
    const cleaned = normalizeAllowlist(targets);
    if (cleaned.length === 0) return false;
    try {
      const res = await api.post('/api/dojo-lab/aertago-control/allowlist', { targets: cleaned }, { timeoutMs: 20000 });
      const next = normalizeAllowlist(res?.allowlist_targets || cleaned);
      setAllowlistLocal(next);
      return true;
    } catch (e) {
      setScanError(e?.message || 'Could not update allowlist');
      return false;
    }
  };

  return (
    <Card className="overflow-hidden border-rose-500/25 bg-gradient-to-br from-card/90 via-card/80 to-rose-950/[0.12] shadow-sm">
      <CardHeader className="border-b border-border/60 bg-rose-950/[0.08] pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex flex-wrap items-center gap-2 text-base text-foreground">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-500/15 ring-1 ring-rose-500/30">
                <Flame className="h-4 w-4 text-rose-300" />
              </span>
              AERTAX Red AI console
              {caps?.enabled ? (
                <Badge variant={caps.ready ? 'success' : 'warning'}>
                  {caps.ready ? 'agent ready' : 'starting / degraded'}
                </Badge>
              ) : (
                <Badge variant="outline">disabled on API</Badge>
              )}
            </CardTitle>
            <CardDescription className="max-w-3xl text-xs leading-relaxed">
              Same LangGraph + MCP protocol as{' '}
              <span className="font-medium text-foreground">aertax-master</span> — wired here for AERTAGO. Session runs
              in-process when <code className="rounded bg-muted px-1">AERTAGO_EMBEDDED_AERTAX_AGENT=true</code>.
              Handles phase approvals, tool confirmations, and mid-run guidance like the standalone webapp.{' '}
              <span className="text-amber-200/90">
                LLM keys: on this pilot, the API must have <code className="rounded bg-muted px-1">AERTA_LLM_ENABLED</code>{' '}
                plus <code className="rounded bg-muted px-1">AERTA_LLM_*</code> (same as V / plan assist) — not the
                standalone AERTAX &quot;Global Settings&quot; page. Presets:{' '}
                <code className="rounded bg-muted px-1">groq</code> (strict TPM on free tier),{' '}
                <code className="rounded bg-muted px-1">ollama</code> (local open-weights),{' '}
                <code className="rounded bg-muted px-1">openrouter</code> or{' '}
                <code className="rounded bg-muted px-1">gemini</code> (hosted; needs API key).
              </span>
            </CardDescription>
          </div>
          {ws.phaseUpdate ? (
            <div className="rounded-md border border-border/70 bg-background/60 px-2 py-1.5 text-[10px] text-muted-foreground">
              <span className="font-medium text-foreground">Phase</span>{' '}
              <span className="font-mono text-foreground">{ws.phaseUpdate.current_phase || '—'}</span>
              <span className="mx-1 text-border">·</span>
              iter {ws.phaseUpdate.iteration_count ?? '—'}
            </div>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-4 text-xs">
        {caps?.error ? (
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-100">
            {String(caps.error)}
          </p>
        ) : null}
        {!caps?.enabled ? (
          <p className="text-muted-foreground">
            Enable the embedded agent on the API, reload Cyber Range, then connect. Overview exposes capability flags
            under <code className="rounded bg-muted px-1">aertago_capabilities.embedded_aertax_agent</code>.
          </p>
        ) : null}

        <div className="rounded-xl border border-cyan-500/35 bg-cyan-950/[0.12] p-3 ring-1 ring-cyan-500/15">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex items-center gap-2 text-[11px] font-semibold text-foreground">
              <Target className="h-4 w-4 shrink-0 text-cyan-300" />
              AERTAGO lab scope
            </div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-8 shrink-0 text-[10px]"
              disabled={!hasLabScopeFacts}
              title={!hasLabScopeFacts ? 'Set a target or allowlist in AERTAGO Live Control, or run a network scan first' : undefined}
              onClick={insertLabContextIntoPrompt}
            >
              Insert into mission prompt
            </Button>
          </div>
          <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground">
            Mirrors <span className="font-medium text-foreground">AERTAGO Live Control</span> (selected target, allowlist, pilot network scan job). Use the button so the embedded agent sees the same facts in your query.
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg border border-border/70 bg-background/50 px-2 py-2">
              <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Selected target</p>
              <p className="mt-0.5 font-mono text-[11px] text-foreground">{effectiveSelectedTarget || '—'}</p>
              {!effectiveSelectedTarget ? (
                <p className="mt-1 text-[9px] text-amber-200/90">
                  Use <span className="font-medium text-foreground">Scan &amp; pick a lab PC</span> below, or select a target in Live Control.
                </p>
              ) : null}
            </div>
            <div className="rounded-lg border border-border/70 bg-background/50 px-2 py-2 sm:col-span-1">
              <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Allowlist</p>
              <p className="mt-0.5 text-[11px] text-foreground">
                {allowlistNorm.length ? (
                  <span>
                    <span className="font-mono">{allowlistNorm.length}</span> target(s)
                  </span>
                ) : (
                  <span className="text-muted-foreground">empty</span>
                )}
              </p>
              {allowlistNorm.length ? (
                <p className="mt-1 max-h-14 overflow-hidden font-mono text-[9px] leading-snug text-muted-foreground">
                  {allowlistNorm.slice(0, 6).join(', ')}
                  {allowlistNorm.length > 6 ? ' …' : ''}
                </p>
              ) : null}
            </div>
            <div className="rounded-lg border border-border/70 bg-background/50 px-2 py-2">
              <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Pilot network scan</p>
              {networkScanTracked?.jobId ? (
                <>
                  <p className="mt-0.5 font-mono text-[10px] text-foreground">
                    {String(networkScanTracked.job?.status || '—')}
                  </p>
                  <p className="mt-0.5 truncate font-mono text-[9px] text-muted-foreground" title={networkScanTracked.jobId}>
                    {networkScanTracked.jobId}
                  </p>
                  {Array.isArray(networkScanTracked.devices) ? (
                    <p className="mt-0.5 text-[9px] text-muted-foreground">{networkScanTracked.devices.length} host(s) in job</p>
                  ) : null}
                </>
              ) : (
                <p className="mt-0.5 text-[11px] text-muted-foreground">no job tracked</p>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-sky-500/25 bg-sky-500/[0.03] p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-[11px] font-semibold text-foreground">
              <Radio className="h-3.5 w-3.5 text-sky-300" />
              Scan & pick a lab PC
              {scanJob?.status ? (
                <Badge variant={scanJob.status === 'completed' ? 'success' : scanJob.status === 'failed' ? 'destructive' : 'warning'}>
                  {scanJob.status}
                </Badge>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-8 text-[10px]"
                disabled={scanLoading || !scanServerOn || allowlistBlocksScans}
                onClick={queueScan}
              >
                {scanLoading ? 'Queueing…' : 'Run scan'}
              </Button>
            </div>
          </div>
          <p className="text-[10px] leading-relaxed text-muted-foreground">
            This queues the same discovery job as the Network Scan page, but stays inside the Red AI console. After results load,
            pick a host to set the target for this console and add it to the AERTAGO allowlist.
          </p>
          {!scanServerOn ? (
            <p className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[10px] text-amber-100">
              Network scanning is off on the API. Set <code className="rounded bg-muted px-1">AERTA_SCAN_ENABLED=true</code> on Render and redeploy.
            </p>
          ) : null}
          {allowlistBlocksScans ? (
            <p className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[10px] text-destructive">
              Allowlist gate is enabled but empty. Set <code className="rounded bg-muted px-1">AERTA_SCAN_ALLOWLIST</code> or disable required mode.
            </p>
          ) : null}
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            <label className="block text-[10px] text-muted-foreground">
              Targets (CIDR or IP)
              <textarea
                className="mt-1 min-h-[72px] w-full rounded-md border border-border bg-background p-2 font-mono text-[10px] text-foreground"
                value={scanTargets}
                onChange={(e) => setScanTargets(e.target.value)}
                placeholder="e.g. 192.168.1.0/24"
                spellCheck={false}
              />
            </label>
            <div className="space-y-2">
              <label className="block text-[10px] text-muted-foreground">
                Ports (optional, comma-separated)
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 font-mono text-[10px] text-foreground"
                  value={scanPorts}
                  onChange={(e) => setScanPorts(e.target.value)}
                  placeholder="leave empty for server default"
                />
              </label>
              <label className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <input type="checkbox" checked={scanAutoIngest} onChange={(e) => setScanAutoIngest(e.target.checked)} />
                Auto-ingest into CMDB
              </label>
              <label className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={scanIncludeNoPorts}
                  onChange={(e) => setScanIncludeNoPorts(e.target.checked)}
                />
                Include reachable hosts with no open ports
              </label>
              {scanError ? (
                <p className="rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-[10px] text-rose-100">
                  {scanError}
                </p>
              ) : null}
              {scanJobId ? (
                <p className="text-[10px] text-muted-foreground">
                  job: <span className="font-mono text-foreground">{scanJobId}</span>
                </p>
              ) : null}
            </div>
          </div>
          {Array.isArray(scanDevices) && scanDevices.length ? (
            <div className="mt-3 rounded-lg border border-border/60 bg-background/40 p-2">
              <p className="mb-2 text-[11px] font-semibold text-foreground">Discovered hosts</p>
              <div className="max-h-[240px] overflow-y-auto rounded-md border border-border/50">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-border/60 text-[10px] uppercase text-muted-foreground">
                      <th className="px-2 py-1.5 text-left">IP</th>
                      <th className="px-2 py-1.5 text-left">Hostname</th>
                      <th className="px-2 py-1.5 text-right">Ports</th>
                      <th className="px-2 py-1.5 text-right">Use</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanDevices.map((d) => {
                      const ip = String(d?.ip || '').trim();
                      const hn = String(d?.hostname || '').trim();
                      const ports = Array.isArray(d?.open_ports) ? d.open_ports.length : 0;
                      const canUse = Boolean(ip);
                      return (
                        <tr key={ip || hn || Math.random()} className="border-b border-border/30 last:border-0">
                          <td className="px-2 py-1.5 font-mono">{ip || '—'}</td>
                          <td className="max-w-[160px] truncate px-2 py-1.5 text-muted-foreground" title={hn}>
                            {hn || '—'}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-muted-foreground">{ports}</td>
                          <td className="px-2 py-1.5 text-right">
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="h-7 px-2 text-[10px]"
                              disabled={!canUse}
                              onClick={async () => {
                                const merged = normalizeAllowlist([...allowlistNorm, ip]);
                                const ok = await persistAllowlist(merged);
                                if (ok) {
                                  setConsoleSelectedTarget(ip);
                                }
                              }}
                            >
                              Select
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground">
                Selecting a host updates the AERTAGO allowlist (server-side) and sets the target for this console’s lab context block.
              </p>
            </div>
          ) : scanJob?.status === 'completed' ? (
            <p className="mt-2 text-[10px] text-muted-foreground">No devices returned (or discovery JSON not available yet).</p>
          ) : null}
        </div>

        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.03] p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-[11px] font-semibold text-foreground">
              <ShieldAlert className="h-3.5 w-3.5 text-emerald-300" />
              Web target pentest (Juice Shop / lab)
            </div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-8 text-[10px]"
              disabled={webLoading || !scanServerOn || allowlistBlocksScans}
              onClick={runWebPentest}
            >
              {webLoading ? 'Running…' : 'Run web check'}
            </Button>
          </div>
          <p className="text-[10px] leading-relaxed text-muted-foreground">
            Bounded web assessment runner for authorized labs. Change the URL in the field below any time before Run web check — nothing
            is locked in. Target host must match <code className="rounded bg-muted px-1">AERTA_WEB_PENTEST_ALLOWLIST</code> on the API.
            Docker names like <code className="rounded bg-muted px-1">juice-shop</code> work when the API runs on the same Docker network;
            from a hosted API use a reachable URL (e.g. <code className="rounded bg-muted px-1">https://demo.owasp-juice.shop</code>).
          </p>
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            <label className="block text-[10px] text-muted-foreground">
              Web URL
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 font-mono text-[10px] text-foreground"
                value={webUrl}
                onChange={(e) => setWebUrl(e.target.value)}
                placeholder="http://juice-shop:3000"
                spellCheck={false}
              />
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <input type="checkbox" checked={webDeep} onChange={(e) => setWebDeep(e.target.checked)} />
                Deep (fetch extra common paths)
              </label>
              {webError ? (
                <p className="rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-[10px] text-rose-100">
                  {webError}
                </p>
              ) : null}
            </div>
          </div>
          {webResult ? (
            <div className="mt-3 rounded-lg border border-border/60 bg-background/40 p-2">
              <p className="mb-2 text-[11px] font-semibold text-foreground">Web findings (summary)</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="rounded-md border border-border/60 bg-background/60 p-2">
                  <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Final URL</p>
                  <p className="mt-0.5 break-all font-mono text-[10px] text-foreground">{webResult.final_url || webResult.target_url}</p>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    status <span className="font-mono text-foreground">{String(webResult.status_code ?? '—')}</span>
                    {webResult.redirected ? <span className="ml-2 text-amber-200/90">(redirected)</span> : null}
                  </p>
                </div>
                <div className="rounded-md border border-border/60 bg-background/60 p-2">
                  <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Security headers</p>
                  <p className="mt-1 font-mono text-[9px] leading-snug text-muted-foreground">
                    {Object.keys(webResult.headers || {}).length
                      ? Object.keys(webResult.headers)
                          .slice(0, 8)
                          .join(', ')
                      : '—'}
                  </p>
                </div>
              </div>
              {Array.isArray(webResult.paths) && webResult.paths.length ? (
                <div className="mt-2 rounded-md border border-border/50">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="border-b border-border/60 text-[9px] uppercase text-muted-foreground">
                        <th className="px-2 py-1.5 text-left">Path</th>
                        <th className="px-2 py-1.5 text-right">Status</th>
                        <th className="px-2 py-1.5 text-left">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {webResult.paths.slice(0, 12).map((p) => (
                        <tr key={p.path} className="border-b border-border/30 last:border-0">
                          <td className="px-2 py-1.5 font-mono">{p.path}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-muted-foreground">
                            {p.status_code != null ? String(p.status_code) : p.error || '—'}
                          </td>
                          <td className="px-2 py-1.5 text-muted-foreground">{p.content_type || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : null}

        </div>

        <div className="grid gap-3 lg:grid-cols-12">
          {/* Session + connect */}
          <div className="space-y-2 rounded-xl border border-border/70 bg-background/40 p-3 lg:col-span-5 animate-fade-in-up motion-reduce:animate-none">
            <div className="flex items-center gap-2 text-[11px] font-semibold text-foreground">
              <Radio className="h-3.5 w-3.5 text-rose-300" />
              Session &amp; socket
              <Badge
                variant={liveStatus.kind === 'offline' ? 'outline' : liveStatus.kind === 'ready' ? 'success' : 'secondary'}
                className={`ml-auto h-5 px-1.5 text-[9px] ${
                  liveStatus.kind === 'tool' || liveStatus.kind === 'thinking' || liveStatus.kind === 'handshaking'
                    ? 'animate-pulse-glow motion-reduce:animate-none'
                    : ''
                }`}
              >
                {liveStatus.label}
              </Badge>
            </div>
            <label className="block text-muted-foreground">
              project_id
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 font-mono text-[11px] text-foreground"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                placeholder={context?.customer_id || 'default'}
              />
            </label>
            <label className="block text-muted-foreground">
              session_id
              <div className="mt-1 flex gap-1">
                <input
                  className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-2 font-mono text-[11px] text-foreground"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="shrink-0 px-2 text-[10px]"
                  onClick={() =>
                    setSessionId(
                      typeof crypto !== 'undefined' && crypto.randomUUID
                        ? crypto.randomUUID()
                        : `sess-${Date.now()}`,
                    )
                  }
                >
                  New
                </Button>
              </div>
            </label>
            <label className="block text-muted-foreground">
              Optional Neo4j scope — <code className="text-[10px]">graph_view_cypher</code>
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 font-mono text-[10px] text-foreground"
                value={graphCypher}
                onChange={(e) => setGraphCypher(e.target.value)}
                placeholder="MATCH (n) RETURN n LIMIT 25 — only if Neo4j configured"
              />
            </label>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                className="gap-1"
                disabled={!caps?.enabled || ws.wsState === 'connecting'}
                onClick={() =>
                  ws.connect({
                    userId: user?.email || user?.sub || `user-${context?.customer_id || 'default'}`,
                    projectId: projectId || context?.customer_id || 'default',
                    sessionId,
                    graphViewCypher: graphCypher.trim() || undefined,
                  })
                }
              >
                {ws.wsState === 'connecting' ? 'Connecting…' : 'Connect'}
              </Button>
              <Button type="button" size="sm" variant="secondary" onClick={() => ws.disconnect()}>
                Disconnect
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => ws.sendPing()}>
                Ping
              </Button>
              <Button type="button" size="sm" variant="ghost" className="text-muted-foreground" onClick={() => ws.clearEvents()}>
                Clear feed
              </Button>
              <Button
                type="button"
                size="sm"
                variant={ws.autoReconnectEnabled ? 'secondary' : 'outline'}
                className="text-[10px]"
                onClick={() => ws.setAutoReconnectEnabled(!ws.autoReconnectEnabled)}
              >
                Auto‑reconnect: {ws.autoReconnectEnabled ? 'On' : 'Off'}
              </Button>
              {ws.agentInited ? (
                <Badge variant="success" className="text-[10px]">
                  connected
                </Badge>
              ) : ws.wsState === 'open' ? (
                <Badge variant="secondary" className="text-[10px] animate-pulse-glow motion-reduce:animate-none">
                  handshaking…
                </Badge>
              ) : null}
            </div>
          </div>

          {/* Query + guidance */}
          <div className="space-y-2 rounded-xl border border-border/70 bg-background/40 p-3 lg:col-span-7 animate-fade-in-up motion-reduce:animate-none">
            <div className="flex items-center gap-2 text-[11px] font-semibold text-foreground">
              <MessageSquare className="h-3.5 w-3.5 text-violet-300" />
              Mission prompt
            </div>
            <textarea
              className="min-h-[88px] w-full rounded-md border border-border bg-background p-2 text-[11px] leading-relaxed text-foreground"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              spellCheck={false}
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" disabled={!ws.agentInited} onClick={() => ws.sendQuery(query)}>
                {ws.agentInited && ws.lastEventAt && Date.now() - ws.lastEventAt < 1500 ? 'Working…' : 'Send query'}
              </Button>
              {ws.lastLatencyMs != null ? (
                <Badge variant="outline" className="h-8 px-2 text-[10px]">
                  latency {ws.lastLatencyMs}ms
                </Badge>
              ) : null}
              <Button type="button" size="sm" variant="outline" disabled={!ws.agentInited} onClick={() => ws.sendStop()}>
                <StopCircle className="mr-1 h-3.5 w-3.5" />
                Stop
              </Button>
              <Button type="button" size="sm" variant="secondary" disabled={!ws.agentInited} onClick={() => ws.sendResume()}>
                Resume
              </Button>
            </div>
            <div className="rounded-lg border border-dashed border-violet-500/30 bg-violet-500/[0.04] p-2">
              <p className="text-[10px] font-medium text-violet-200/90">Steer while running (guidance)</p>
              <div className="mt-1 flex gap-1">
                <input
                  className="min-w-0 flex-1 rounded border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
                  value={guidance}
                  onChange={(e) => setGuidance(e.target.value)}
                  placeholder="e.g. Prefer read-only recon; do not pivot outside allowlist."
                />
                <Button type="button" size="sm" variant="secondary" disabled={!ws.agentInited} onClick={() => ws.sendGuidance(guidance)}>
                  Send
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive gates */}
        {(ws.approvalRequest || ws.questionRequest || toolConfirmationRequest) && (
          <div className="grid gap-3 md:grid-cols-3">
            {ws.approvalRequest ? (
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/[0.07] p-3">
                <div className="mb-2 flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-amber-200" />
                  <span className="text-[11px] font-semibold text-foreground">Phase approval</span>
                </div>
                <pre className="mb-2 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded bg-background/70 p-2 font-mono text-[10px] text-muted-foreground">
                  {summarizeEvent('approval_request', ws.approvalRequest)}
                </pre>
                <textarea
                  className="mb-2 min-h-[48px] w-full rounded border border-border bg-background p-2 font-mono text-[10px] text-foreground"
                  value={approvalModify}
                  onChange={(e) => setApprovalModify(e.target.value)}
                  placeholder="Modification text (if you choose Modify)"
                />
                <div className="flex flex-wrap gap-1">
                  <Button type="button" size="sm" className="h-8 text-[10px]" disabled={!ws.agentInited} onClick={() => ws.sendApproval('approve')}>
                    Approve
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-8 text-[10px]"
                    disabled={!ws.agentInited}
                    onClick={() => ws.sendApproval('modify', approvalModify)}
                  >
                    Modify
                  </Button>
                  <Button type="button" size="sm" variant="destructive" className="h-8 text-[10px]" disabled={!ws.agentInited} onClick={() => ws.sendApproval('abort')}>
                    Abort
                  </Button>
                </div>
              </div>
            ) : null}

            {ws.questionRequest ? (
              <div className="rounded-xl border border-sky-500/40 bg-sky-500/[0.07] p-3">
                <div className="mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-sky-200" />
                  <span className="text-[11px] font-semibold text-foreground">Agent question</span>
                </div>
                <pre className="mb-2 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded bg-background/70 p-2 font-mono text-[10px] text-muted-foreground">
                  {summarizeEvent('question_request', ws.questionRequest)}
                </pre>
                <textarea
                  className="mb-2 min-h-[56px] w-full rounded border border-border bg-background p-2 text-[11px] text-foreground"
                  value={questionAnswer}
                  onChange={(e) => setQuestionAnswer(e.target.value)}
                />
                <Button type="button" size="sm" disabled={!ws.agentInited} onClick={() => ws.sendAnswer(questionAnswer)}>
                  Submit answer
                </Button>
              </div>
            ) : null}

            {toolConfirmationRequest ? (
              <div className="rounded-xl border border-rose-500/45 bg-rose-500/[0.08] p-3 md:col-span-1">
                <div className="mb-2 flex items-center gap-2">
                  <Cpu className="h-4 w-4 text-rose-200" />
                  <span className="text-[11px] font-semibold text-foreground">Tool confirmation</span>
                  <Badge variant="outline" className="text-[9px]">
                    {toolConfirmationRequest.mode || 'single'}
                  </Badge>
                </div>
                <ul className="mb-2 max-h-28 space-y-1 overflow-auto text-[10px] text-muted-foreground">
                  {(Array.isArray(toolsPending) ? toolsPending : []).map((t, i) => (
                    <li key={i} className="rounded border border-border/50 bg-background/60 px-2 py-1 font-mono">
                      {(t && t.tool_name) || 'tool'}
                    </li>
                  ))}
                </ul>
                <label className="mb-1 block text-[10px] text-muted-foreground">
                  modifications JSON (Modify path)
                  <textarea
                    className="mt-1 min-h-[44px] w-full rounded border border-border bg-background p-2 font-mono text-[10px] text-foreground"
                    value={toolModifyJson}
                    onChange={(e) => setToolModifyJson(e.target.value)}
                  />
                </label>
                <div className="flex flex-wrap gap-1">
                  <Button type="button" size="sm" className="h-8 text-[10px]" disabled={!ws.agentInited} onClick={() => ws.sendToolConfirmation('approve')}>
                    Approve tools
                  </Button>
                  <Button type="button" size="sm" variant="destructive" className="h-8 text-[10px]" disabled={!ws.agentInited} onClick={() => ws.sendToolConfirmation('reject')}>
                    Reject
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-8 text-[10px]"
                    disabled={!ws.agentInited}
                    onClick={() => {
                      try {
                        const mod = JSON.parse(toolModifyJson || '{}');
                        ws.sendToolConfirmation('modify', mod);
                      } catch {
                        /* ignore invalid JSON */
                      }
                    }}
                  >
                    Modify
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}

        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-xl border border-border/70 bg-background/30 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold text-foreground">Live reasoning stream</span>
              {ws.streamingThought ? <Badge variant="secondary">streaming</Badge> : latestThinkingText ? (
                <Badge variant="outline">latest</Badge>
              ) : null}
            </div>
            <pre className="max-h-[200px] min-h-[80px] overflow-auto whitespace-pre-wrap break-words rounded-md border border-border/60 bg-muted/20 p-2 font-mono text-[10px] leading-relaxed text-foreground">
              {ws.streamingThought || latestThinkingText || '— waiting for agent thoughts —'}
            </pre>
            {!ws.agentInited ? (
              <div className="mt-2 space-y-2">
                <TinySkeleton />
                <TinySkeleton className="w-5/6" />
                <TinySkeleton className="w-2/3" />
              </div>
            ) : null}
            {!ws.streamingThought && !latestThinkingText ? (
              <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground">
                No <code className="rounded bg-muted px-1">thinking</code> events received yet. Some runs respond without emitting intermediate reasoning
                (or provider/tooling may not stream). The Activity feed below will still show tool execution when the agent runs tools.
              </p>
            ) : null}
          </div>
          <div className="rounded-xl border border-border/70 bg-background/30 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[11px] font-semibold text-foreground">Todo board</span>
              <Badge variant="outline" className="text-[9px]">
                {todosNormalized.length} items
              </Badge>
            </div>
            <div className="grid max-h-[200px] gap-2 overflow-auto md:grid-cols-2">
              {[
                ['in_progress', 'In progress'],
                ['pending', 'Queued'],
                ['blocked', 'Blocked'],
                ['completed', 'Done'],
              ].map(([key, label]) => (
                <div key={key} className="rounded-lg border border-border/50 bg-background/40 p-2">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-[10px] font-semibold text-foreground">{label}</span>
                    <Badge variant="outline" className="h-5 px-1.5 text-[9px]">
                      {todoGroups[key]?.length ?? 0}
                    </Badge>
                  </div>
                  <ul className="space-y-1 text-[10px] text-muted-foreground">
                    {(todoGroups[key] || []).slice(0, 12).map((t) => (
                      <li key={t.id} className="rounded border border-border/40 bg-background/50 px-2 py-1">
                        <div className="flex items-start justify-between gap-2">
                          <span className="min-w-0 break-words text-foreground/90">{t.description}</span>
                          <Badge variant={priorityBadgeVariant(t.priority)} className="h-5 shrink-0 px-1.5 text-[9px]">
                            {t.priority}
                          </Badge>
                        </div>
                        <div className="mt-0.5 truncate font-mono text-[9px] text-muted-foreground">{t.id}</div>
                      </li>
                    ))}
                    {(todoGroups[key] || []).length === 0 ? (
                      <li className="text-[9px] text-muted-foreground">—</li>
                    ) : null}
                  </ul>
                </div>
              ))}
            </div>
            {(!ws.todoList || ws.todoList.length === 0) && derivedTodos.length ? (
              <p className="mt-2 text-[10px] text-muted-foreground">
                Showing <span className="font-medium text-foreground">derived</span> next steps from recent tool/plan events. When the agent emits
                <code className="ml-1 rounded bg-muted px-1">todo_update</code>, this board will switch to the agent-managed list.
              </p>
            ) : null}
          </div>
        </div>

        <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.03] p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-[11px] font-semibold text-foreground">
              <Cpu className="h-4 w-4 text-amber-200" />
              Tool runs
              <Badge variant={runningCount ? 'warning' : 'outline'} className="h-5 px-1.5 text-[9px]">
                {runningCount ? `${runningCount} running` : 'idle'}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant={toolRunsShowOutput ? 'secondary' : 'outline'}
                className="h-8 text-[10px]"
                onClick={() => setToolRunsShowOutput((v) => !v)}
              >
                {toolRunsShowOutput ? 'Hide output' : 'Show output'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 text-[10px]"
                onClick={() => setToolRunsExpanded(new Set(toolRunsExpanded.size ? [] : toolRuns.map((r) => r.key)))}
              >
                {toolRunsExpanded.size ? 'Collapse all' : 'Expand all'}
              </Button>
            </div>
          </div>
          <p className="text-[10px] leading-relaxed text-muted-foreground">
            Live tool execution grouped by <code className="rounded bg-muted px-1">tool_start</code> /{' '}
            <code className="rounded bg-muted px-1">tool_output_chunk</code> / <code className="rounded bg-muted px-1">tool_complete</code>.
            This is the primary “live process” view when the agent uses cybersecurity tooling.
          </p>
          <div className="mt-2 max-h-[420px] space-y-2 overflow-y-auto rounded-lg border border-border/60 bg-background/30 p-2">
            {toolRuns.length === 0 ? (
              <p className="p-2 text-[11px] text-muted-foreground">
                No tool runs yet. Ask the agent to perform a recon action (e.g. “Run an allowlist-only service discovery on the selected target.”).
              </p>
            ) : (
              <div className="space-y-2">
                {toolWaves.map((w) => {
                  const runs = toolRunsByWave.get(w.wave_id) || [];
                  const done = runs.filter((r) => r.status === 'complete' || r.status === 'failed').length;
                  const total = w.complete?.total_steps ?? w.tool_count ?? runs.length;
                  const ok = runs.filter((r) => r.status === 'complete').length;
                  const fail = runs.filter((r) => r.status === 'failed').length;
                  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
                  const waveTitle = `wave ${String(w.wave_id).slice(0, 10)}${String(w.wave_id).length > 10 ? '…' : ''}`;

                  return (
                    <div key={w.wave_id} className="rounded-lg border border-sky-500/25 bg-sky-500/[0.03] p-2">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className="h-5 px-1.5 text-[9px]">
                              {waveTitle}
                            </Badge>
                            <Badge variant={done < total ? 'warning' : fail ? 'destructive' : 'success'} className="h-5 px-1.5 text-[9px]">
                              {done < total ? 'running' : fail ? 'completed with failures' : 'completed'}
                            </Badge>
                            <span className="text-[10px] text-muted-foreground">
                              {ok}/{total} ok · {fail} fail
                            </span>
                          </div>
                          {w.plan_rationale ? (
                            <p className="mt-1 line-clamp-2 text-[10px] leading-relaxed text-muted-foreground">{w.plan_rationale}</p>
                          ) : null}
                          <div className="mt-2 h-2 overflow-hidden rounded bg-muted/30">
                            <div className="h-full bg-sky-500/60" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>

                      {w.analysis?.interpretation ? (
                        <div className="mt-2 rounded-md border border-border/60 bg-background/40 p-2">
                          <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Wave analysis</p>
                          <p className="mt-1 whitespace-pre-wrap break-words text-[10px] text-muted-foreground">
                            {String(w.analysis.interpretation).slice(0, 800)}
                            {String(w.analysis.interpretation).length > 800 ? '…' : ''}
                          </p>
                        </div>
                      ) : null}

                      <div className="mt-2 space-y-2">
                        {runs.length === 0 ? (
                          <p className="text-[10px] text-muted-foreground">No tool steps received for this wave yet.</p>
                        ) : (
                          runs.map((r) => {
                            const expanded = toolRunsExpanded.has(r.key);
                            const statusBadge =
                              r.status === 'running'
                                ? { variant: 'warning', label: 'running' }
                                : r.status === 'failed'
                                  ? { variant: 'destructive', label: 'failed' }
                                  : { variant: 'success', label: 'complete' };
                            const when = r.started_at ? new Date(r.started_at).toLocaleTimeString() : '—';
                            const stepLabel = r.step_index != null ? `step ${r.step_index}` : null;

                            return (
                              <div key={r.key} className={`rounded-lg border border-border/60 bg-background/50 p-2 ${eventAccent('tool_start')}`}>
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <Badge variant={statusBadge.variant} className="h-5 px-1.5 text-[9px]">
                                        {statusBadge.label}
                                      </Badge>
                                      <span className="font-mono text-[10px] text-foreground">{r.tool_name}</span>
                                      {stepLabel ? (
                                        <Badge variant="outline" className="h-5 px-1.5 text-[9px]">
                                          {stepLabel}
                                        </Badge>
                                      ) : null}
                                      <span className="font-mono text-[9px] text-muted-foreground">{when}</span>
                                    </div>
                                    <p className="mt-1 line-clamp-2 font-mono text-[9px] text-muted-foreground">
                                      args: {JSON.stringify(r.tool_args || {}).slice(0, 420)}
                                      {JSON.stringify(r.tool_args || {}).length > 420 ? '…' : ''}
                                    </p>
                                    {r.complete?.output_summary ? (
                                      <p className="mt-1 whitespace-pre-wrap break-words text-[10px] text-muted-foreground">
                                        {String(r.complete.output_summary).slice(0, 500)}
                                        {String(r.complete.output_summary).length > 500 ? '…' : ''}
                                      </p>
                                    ) : null}
                                  </div>
                                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                                    <Badge variant="outline" className="h-5 px-1.5 text-[9px]">
                                      {r.chunks ? `${r.chunks} chunk(s)` : 'no chunks'}
                                    </Badge>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-8 text-[10px]"
                                      onClick={() => {
                                        setToolRunsExpanded((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(r.key)) next.delete(r.key);
                                          else next.add(r.key);
                                          return next;
                                        });
                                      }}
                                    >
                                      {expanded ? 'Collapse' : 'Expand'}
                                    </Button>
                                  </div>
                                </div>
                                <div
                                  className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none ${
                                    expanded ? 'mt-2 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                                  }`}
                                >
                                  <div className="overflow-hidden">
                                    <div className="grid gap-2 md:grid-cols-2">
                                    <div className="rounded-md border border-border/60 bg-background/60 p-2">
                                      <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Actionable findings</p>
                                      <ul className="mt-1 space-y-1 text-[10px] text-muted-foreground">
                                        {(Array.isArray(r.complete?.actionable_findings) ? r.complete.actionable_findings : [])
                                          .slice(0, 8)
                                          .map((x, i) => (
                                            <li key={i} className="break-words">
                                              - {String(x)}
                                            </li>
                                          ))}
                                        {(!r.complete?.actionable_findings || r.complete.actionable_findings.length === 0) && (
                                          <li className="text-muted-foreground">—</li>
                                        )}
                                      </ul>
                                    </div>
                                    <div className="rounded-md border border-border/60 bg-background/60 p-2">
                                      <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Recommended next steps</p>
                                      <ul className="mt-1 space-y-1 text-[10px] text-muted-foreground">
                                        {(Array.isArray(r.complete?.recommended_next_steps) ? r.complete.recommended_next_steps : [])
                                          .slice(0, 8)
                                          .map((x, i) => (
                                            <li key={i} className="break-words">
                                              - {String(x)}
                                            </li>
                                          ))}
                                        {(!r.complete?.recommended_next_steps || r.complete.recommended_next_steps.length === 0) && (
                                          <li className="text-muted-foreground">—</li>
                                        )}
                                      </ul>
                                    </div>
                                    {toolRunsShowOutput ? (
                                      <div className="md:col-span-2">
                                        <p className="mb-1 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Tool output (tail)</p>
                                        <pre className="max-h-[160px] overflow-auto whitespace-pre-wrap break-words rounded-md border border-border/60 bg-muted/20 p-2 font-mono text-[10px] leading-relaxed text-foreground">
                                          {r.output ? r.output : '—'}
                                        </pre>
                                      </div>
                                    ) : null}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}

                {adHocToolRuns.length ? (
                  <div className="rounded-lg border border-border/60 bg-background/40 p-2">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-foreground">Ad‑hoc tools</span>
                      <Badge variant="outline" className="h-5 px-1.5 text-[9px]">
                        {adHocToolRuns.length}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {adHocToolRuns.map((r) => {
                        const expanded = toolRunsExpanded.has(r.key);
                        const statusBadge =
                          r.status === 'running'
                            ? { variant: 'warning', label: 'running' }
                            : r.status === 'failed'
                              ? { variant: 'destructive', label: 'failed' }
                              : { variant: 'success', label: 'complete' };
                        const when = r.started_at ? new Date(r.started_at).toLocaleTimeString() : '—';
                        return (
                          <div key={r.key} className={`rounded-lg border border-border/60 bg-background/50 p-2 ${eventAccent('tool_start')}`}>
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge variant={statusBadge.variant} className="h-5 px-1.5 text-[9px]">
                                    {statusBadge.label}
                                  </Badge>
                                  <span className="font-mono text-[10px] text-foreground">{r.tool_name}</span>
                                  <span className="font-mono text-[9px] text-muted-foreground">{when}</span>
                                </div>
                                <p className="mt-1 line-clamp-2 font-mono text-[9px] text-muted-foreground">
                                  args: {JSON.stringify(r.tool_args || {}).slice(0, 420)}
                                  {JSON.stringify(r.tool_args || {}).length > 420 ? '…' : ''}
                                </p>
                              </div>
                              <div className="flex shrink-0 flex-wrap items-center gap-2">
                                <Badge variant="outline" className="h-5 px-1.5 text-[9px]">
                                  {r.chunks ? `${r.chunks} chunk(s)` : 'no chunks'}
                                </Badge>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8 text-[10px]"
                                  onClick={() => {
                                    setToolRunsExpanded((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(r.key)) next.delete(r.key);
                                      else next.add(r.key);
                                      return next;
                                    });
                                  }}
                                >
                                  {expanded ? 'Collapse' : 'Expand'}
                                </Button>
                              </div>
                            </div>
                            {expanded && toolRunsShowOutput ? (
                              <div className="mt-2">
                                <p className="mb-1 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Tool output (tail)</p>
                                <pre className="max-h-[160px] overflow-auto whitespace-pre-wrap break-words rounded-md border border-border/60 bg-muted/20 p-2 font-mono text-[10px] leading-relaxed text-foreground">
                                  {r.output ? r.output : '—'}
                                </pre>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold text-foreground">
            <Flame className="h-3.5 w-3.5 text-rose-300" />
            Activity feed
          </div>
          <div className="max-h-[280px] space-y-1.5 overflow-y-auto rounded-xl border border-border/60 bg-background/40 p-2">
            {feed.length === 0 ? (
              <p className="p-2 text-[11px] text-muted-foreground">Connect and send a query to populate the feed.</p>
            ) : (
              feed
                .slice()
                .reverse()
                .map((e, idx) => (
                  <div
                    key={e.id}
                    className={`border-l-2 px-2 py-1.5 text-[10px] ${eventAccent(e.type)} animate-fade-in-up motion-reduce:animate-none`}
                    style={{ animationDelay: `${Math.min(0.26, idx * 0.02)}s` }}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-[9px] text-muted-foreground">{new Date(e.at).toLocaleTimeString()}</span>
                      <Badge variant="outline" className="h-5 px-1.5 font-mono text-[9px]">
                        {e.type}
                      </Badge>
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-muted-foreground">{summarizeEvent(e.type, e.payload)}</p>
                  </div>
                ))
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
